import React, { useEffect, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import useAppStore from '../store/appStore';
import { Page, Section, PageTitle } from '../components/UI';
import type { DownloadItem } from '../store/types';
import {
    normalizeEmbedSrc,
    extractVimeoId,
    isImageMediaUrl,
    isVideoEmbedUrl,
    getEmbedUrl,
    fetchVimeoPosterUrl,
    getVimeoPosterUrl,
    getYoutubePosterUrl,
    buildAutoplayEmbedUrl,
    requestElementFullscreen,
    exitElementFullscreen,
} from '../utils/videoEmbed';
import { LinkifiedText } from '../utils/linkifyText';

// ============================================================================
// STYLES: EXECUTIVE CARBON EDITION (NOBLE SLATE v11.0)
// ============================================================================
const AcademyStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700;800&family=Inter:wght@300;400;600;800&display=swap');

        .font-sans { font-family: 'Inter', sans-serif; }
        .font-mono-numbers { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        
        .bg-deep-space { background-color: #030303 !important; }
        html:not(.dark) .bg-deep-space { background-color: var(--pe-bg-primary) !important; }
        html:not(.dark) .carbon-weave-bg { background-color: var(--pe-bg-elevated); background-image: none; }
        html:not(.dark) .carbon-panel { background-color: var(--pe-bg-card); border-color: var(--pe-border-subtle); }
        html:not(.dark) .carbon-panel::after { background: none; }
        
        .carbon-weave-bg {
            background-color: #111;
            background-image: 
                radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
                radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent) !important;
            background-size: 20px 20px !important;
        }

        .carbon-panel {
            position: relative;
            border-radius: 1.5rem;
            border: 1px solid #2a2a2a;
            box-shadow: 0 30px 60px -15px rgba(0,0,0,1), inset 0 2px 5px rgba(255,255,255,0.05);
        }

        .carbon-panel::after {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.6));
            border-radius: inherit;
            pointer-events: none;
        }

        .carbon-content { position: relative; z-index: 10; }

        /* ========================================================================
            FINALER FIX: BLAUER AUSSEN-WRAPPER WIRD SCHWARZ
            ======================================================================== */

        html, body, #root, #__next, main {
            background-color: #030303 !important;
            background: #030303 !important;
        }

        body:has(.academy-carbon-shell),
        body:has(.academy-carbon-shell) #root,
        body:has(.academy-carbon-shell) #__next,
        body:has(.academy-carbon-shell) main,
        body:has(.academy-carbon-shell) div:has(.academy-carbon-shell),
        body:has(.academy-carbon-shell) section:has(.academy-carbon-shell),
        body:has(.academy-carbon-shell) [class*="page"]:has(.academy-carbon-shell),
        body:has(.academy-carbon-shell) [class*="Page"]:has(.academy-carbon-shell),
        body:has(.academy-carbon-shell) [class*="layout"]:has(.academy-carbon-shell),
        body:has(.academy-carbon-shell) [class*="Layout"]:has(.academy-carbon-shell),
        body:has(.academy-carbon-shell) [class*="wrapper"]:has(.academy-carbon-shell),
        body:has(.academy-carbon-shell) [class*="Wrapper"]:has(.academy-carbon-shell) {
            background-color: #030303 !important;
            background: #030303 !important;
            background-image: none !important;
        }

        .academy-carbon-shell {
            background-color: #030303 !important;
            background: #030303 !important;
            min-height: 100vh;
        }

        .academy-carbon-shell > *,
        .academy-carbon-shell section,
        .academy-carbon-shell section > div,
        .academy-carbon-shell [class*="bg-slate-900"],
        .academy-carbon-shell [class*="bg-slate-950"],
        .academy-carbon-shell [class*="bg-blue-"],
        .academy-carbon-shell [class*="bg-indigo-"],
        .academy-carbon-shell [class*="bg-gray-900"],
        .academy-carbon-shell [class*="bg-gray-950"] {
            background-color: #030303 !important;
        }

        .academy-carbon-shell .carbon-panel {
            background-color: initial;
        }

        .academy-carbon-shell .carbon-weave-bg {
            background-color: #111 !important;
            background-image: 
                radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
                radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent) !important;
            background-size: 20px 20px !important;
        }

        .academy-carbon-shell .bg-black {
            background-color: #000000 !important;
        }

        /* ============================================================
           LIGHT-MODE OVERRIDES (Ring 4b) — Dark Mode bleibt pixelidentisch,
           da alle Regeln nur unter html:not(.dark) greifen.
           ============================================================ */
        html:not(.dark) .bg-deep-space { background-color: #F4F2EF !important; }
        html:not(.dark) .carbon-weave-bg {
            background-color: #FFFFFF !important;
            background-image: none !important;
        }
        html:not(.dark) .carbon-panel {
            border-color: #D6D3D1;
            box-shadow: 0 10px 30px -12px rgba(28,25,23,0.15);
        }
        html:not(.dark) .carbon-panel::after { background: none; }
        html:not(.dark),
        html:not(.dark) body,
        html:not(.dark) #root,
        html:not(.dark) #__next,
        html:not(.dark) main {
            background-color: #F4F2EF !important;
            background: #F4F2EF !important;
        }
        html:not(.dark) body:has(.academy-carbon-shell),
        html:not(.dark) body:has(.academy-carbon-shell) #root,
        html:not(.dark) body:has(.academy-carbon-shell) #__next,
        html:not(.dark) body:has(.academy-carbon-shell) main,
        html:not(.dark) body:has(.academy-carbon-shell) div:has(.academy-carbon-shell),
        html:not(.dark) body:has(.academy-carbon-shell) section:has(.academy-carbon-shell),
        html:not(.dark) body:has(.academy-carbon-shell) [class*="page"]:has(.academy-carbon-shell),
        html:not(.dark) body:has(.academy-carbon-shell) [class*="Page"]:has(.academy-carbon-shell),
        html:not(.dark) body:has(.academy-carbon-shell) [class*="layout"]:has(.academy-carbon-shell),
        html:not(.dark) body:has(.academy-carbon-shell) [class*="Layout"]:has(.academy-carbon-shell),
        html:not(.dark) body:has(.academy-carbon-shell) [class*="wrapper"]:has(.academy-carbon-shell),
        html:not(.dark) body:has(.academy-carbon-shell) [class*="Wrapper"]:has(.academy-carbon-shell) {
            background-color: #F4F2EF !important;
            background: #F4F2EF !important;
            background-image: none !important;
        }
        html:not(.dark) .academy-carbon-shell {
            background-color: #F4F2EF !important;
            background: #F4F2EF !important;
        }
        html:not(.dark) .academy-carbon-shell > *,
        html:not(.dark) .academy-carbon-shell section,
        html:not(.dark) .academy-carbon-shell section > div,
        html:not(.dark) .academy-carbon-shell [class*="bg-slate-900"],
        html:not(.dark) .academy-carbon-shell [class*="bg-slate-950"],
        html:not(.dark) .academy-carbon-shell [class*="bg-blue-"],
        html:not(.dark) .academy-carbon-shell [class*="bg-indigo-"],
        html:not(.dark) .academy-carbon-shell [class*="bg-gray-900"],
        html:not(.dark) .academy-carbon-shell [class*="bg-gray-950"] {
            background-color: transparent !important;
        }
        html:not(.dark) .academy-carbon-shell .carbon-weave-bg {
            background-color: #FFFFFF !important;
            background-image: none !important;
        }
    `}</style>
);

interface TutorialCategory {
    name: string;
}

const AcademyFullscreenButton: React.FC<{ containerRef: React.RefObject<HTMLDivElement> }> = ({ containerRef }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === containerRef.current);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        };
    }, [containerRef]);

    const toggleFullscreen = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        const node = containerRef.current;
        if (!node) return;

        try {
            if (document.fullscreenElement === node) {
                await exitElementFullscreen();
            } else {
                await requestElementFullscreen(node);
            }
        } catch (error) {
            console.error('[AcademyPage] Vollbild fehlgeschlagen', error);
        }
    };

    return (
        <button
            type="button"
            onClick={toggleFullscreen}
            className="absolute top-2 right-2 z-20 flex items-center gap-1.5 rounded-md bg-black/75 hover:bg-black/90 border border-slate-600 text-white px-2.5 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors"
            aria-label={isFullscreen ? 'Vollbild beenden' : 'Vollbild'}
        >
            {isFullscreen ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                    <path d="M9 3H5a2 2 0 0 0-2 2v4" /><path d="M15 3h4a2 2 0 0 1 2 2v4" /><path d="M9 21H5a2 2 0 0 1-2-2v-4" /><path d="M15 21h4a2 2 0 0 0 2-2v-4" />
                </svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                    <path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" /><path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" />
                </svg>
            )}
            {isFullscreen ? 'Beenden' : 'Vollbild'}
        </button>
    );
};

const AcademyMediaPlaceholder: React.FC<{ label: string }> = ({ label }) => (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-800 text-stone-500 dark:text-slate-600">
        <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 mb-2 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        <span className="font-black text-[10px] uppercase tracking-widest opacity-70 text-center px-4">{label}</span>
    </div>
);

const AcademyMediaSlot: React.FC<{ video: any }> = ({ video }) => {
    const playerRef = useRef<HTMLDivElement>(null);
    const [failed, setFailed] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const embedSrc = normalizeEmbedSrc(video.src || '');
    // PEBase-kompatibel: thumbnail | imageUrl | thumbnailUrl (Upload)
    const imageSrc = (
        video.thumbnail ||
        video.imageUrl ||
        video.thumbnailUrl ||
        ''
    ).trim();
    const title = video.title || 'Academy-Inhalt';
    const resolvedImageSrc = isImageMediaUrl(embedSrc) ? embedSrc : imageSrc;
    const isVideoEmbed = isVideoEmbedUrl(embedSrc);
    const ytPoster = getYoutubePosterUrl(embedSrc);
    // Sofort-Poster wie PEBase (vumbnail) — kein Warten auf oEmbed
    const instantVimeoPoster = getVimeoPosterUrl(embedSrc);
    const [posterUrl, setPosterUrl] = useState<string | null>(
        () => ytPoster || resolvedImageSrc || instantVimeoPoster || null
    );

    useEffect(() => {
        let cancelled = false;
        const immediate = ytPoster || resolvedImageSrc || instantVimeoPoster || null;
        setPosterUrl(immediate);

        // Optional: oEmbed-Upgrade (wie PEBase hydratePosters)
        if (!ytPoster && !resolvedImageSrc && extractVimeoId(embedSrc)) {
            fetchVimeoPosterUrl(embedSrc).then((url) => {
                if (!cancelled && url) setPosterUrl(url);
            });
        }

        return () => {
            cancelled = true;
        };
    }, [embedSrc, ytPoster, resolvedImageSrc, instantVimeoPoster]);

    if (failed) {
        return <AcademyMediaPlaceholder label="Medium nicht verfügbar" />;
    }

    if (!embedSrc && !imageSrc) {
        return <AcademyMediaPlaceholder label="Video folgt" />;
    }

    if (resolvedImageSrc && (isImageMediaUrl(embedSrc) || !embedSrc)) {
        return (
            <img
                src={resolvedImageSrc}
                alt={title}
                referrerPolicy="no-referrer"
                className="absolute inset-0 w-full h-full object-cover"
                onError={() => {
                    console.error('[AcademyPage] Bild konnte nicht geladen werden', { src: resolvedImageSrc, title });
                    setFailed(true);
                }}
            />
        );
    }

    if (isVideoEmbed && !isPlaying) {
        const previewSrc = posterUrl || '';
        return (
            <button
                type="button"
                onClick={() => setIsPlaying(true)}
                className="absolute inset-0 w-full h-full border-0 p-0 cursor-pointer bg-slate-800"
                aria-label={`${title} abspielen`}
            >
                {previewSrc ? (
                    <img
                        src={previewSrc}
                        alt={title}
                        referrerPolicy="no-referrer"
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => {
                            // vumbnail → oEmbed schon gelaufen; Bild ausblenden, Play-Button bleibt
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                    />
                ) : (
                    <AcademyMediaPlaceholder label="Video laden" />
                )}
                <span className="absolute inset-0 flex items-center justify-center bg-black/35">
                    <span className="w-14 h-14 rounded-full bg-[#E6C299] text-black flex items-center justify-center shadow-lg">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7 ml-0.5">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </span>
                </span>
            </button>
        );
    }

    if (isVideoEmbed && isPlaying) {
        return (
            <div ref={playerRef} className="absolute inset-0 w-full h-full bg-black">
                <iframe
                    className="w-full h-full border-0"
                    src={buildAutoplayEmbedUrl(embedSrc)}
                    title={title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                />
                <AcademyFullscreenButton containerRef={playerRef} />
            </div>
        );
    }

    return (
        <div ref={playerRef} className="absolute inset-0 w-full h-full bg-black">
            <iframe
                className="w-full h-full border-0"
                src={getEmbedUrl(embedSrc)}
                title={title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
            />
            <AcademyFullscreenButton containerRef={playerRef} />
        </div>
    );
};

const AcademyPage: React.FC = () => {
    const { settings } = useAppStore(state => ({
        settings: state.settings,
    }));

    const tutorialVideos = ((settings as any).tutorialVideos || []).filter(
        (video: any) => video?.isPublic !== false
    );
    const downloadItems: DownloadItem[] = Array.isArray(settings?.downloadItems)
        ? (settings.downloadItems as DownloadItem[])
        : [];

    const [activeTab, setActiveTab] = useState<'videos' | 'downloads'>('videos');
    
    // 💉 CHIRURGIE: Kategorien-Datenstruktur parsen (Abwärtskompatibel zu alten Strings)
    const rawCategories = (settings as any).tutorialCategories || [];
    const tutorialCategories: TutorialCategory[] = rawCategories.map((c: any) => {
        if (typeof c === 'string') return { name: c };
        return { name: c.name };
    });

    if (tutorialCategories.length === 0) {
        tutorialCategories.push({ name: 'Allgemein' });
    }

    const visibleCategoryNames = tutorialCategories.map(c => c.name);

    const videosByCategory: Record<string, any[]> = {};
    visibleCategoryNames.forEach(name => videosByCategory[name] = []);
    const orphanVideos: any[] = [];

    // Videos den Kategorien zuordnen
    tutorialVideos.forEach((video: any) => {
        const catName = video.category || visibleCategoryNames[0];
        
        if (visibleCategoryNames.includes(catName)) {
            videosByCategory[catName].push(video);
        } else {
            // Die Kategorie des Videos existiert nicht mehr im System -> Orphan
            orphanVideos.push(video);
        }
    });

    const rawDownloadCategories = (settings as any).downloadCategories || [];
    const downloadCategories: TutorialCategory[] = rawDownloadCategories.map((c: any) => {
        if (typeof c === 'string') return { name: c };
        return { name: c.name };
    });
    if (downloadCategories.length === 0) {
        downloadCategories.push({ name: 'Allgemein' });
    }
    const downloadCategoryNames = downloadCategories.map((c) => c.name);

    const downloadsByCategory: Record<string, DownloadItem[]> = {};
    downloadCategoryNames.forEach((name) => {
        downloadsByCategory[name] = [];
    });
    const orphanDownloads: DownloadItem[] = [];
    downloadItems.forEach((item) => {
        const catName = item.category || downloadCategoryNames[0];
        if (downloadCategoryNames.includes(catName)) {
            downloadsByCategory[catName].push(item);
        } else {
            orphanDownloads.push(item);
        }
    });

    const fileTypeBadge = (t: string) => {
        const map: Record<string, string> = {
            pdf: 'PDF',
            zip: 'ZIP',
            xlsx: 'Excel',
            image: 'Bild',
            other: 'Datei',
        };
        return map[t] || t.toUpperCase();
    };

    const renderVideoCard = (video: any) => (
        <div key={video.id} className="carbon-panel carbon-weave-bg rounded-2xl overflow-hidden border border-pe-border dark:border-[#2a2a2a] hover:border-pe-border-strong dark:hover:border-[#333] shadow-2xl flex flex-col group transition-all duration-300">
            <div className="carbon-content w-full h-full flex flex-col">
                <div className="relative w-full pt-[56.25%] bg-black border-b border-pe-border dark:border-[#222] shadow-inner">
                    <AcademyMediaSlot video={video} />
                </div>
                <div className="p-5 flex-grow flex flex-col">
                    <h5 className="text-lg font-black text-pe-text dark:text-white mb-2 group-hover:text-[#8D6435] dark:group-hover:text-[#E6C299] transition-colors duration-300 tracking-wide uppercase">{video.title}</h5>
                    <p className="text-pe-muted dark:text-slate-400 text-sm leading-relaxed mb-4">
                        <LinkifiedText text={video.description} />
                    </p>
                </div>
            </div>
        </div>
    );

    const renderDownloadCard = (item: DownloadItem) => {
        const previewSrc =
            item.thumbnailUrl || (item.fileType === 'image' ? item.fileUrl : undefined);

        return (
        <div
            key={item.id}
            className="carbon-panel carbon-weave-bg rounded-2xl overflow-hidden border border-pe-border dark:border-[#2a2a2a] hover:border-pe-border-strong dark:hover:border-[#333] shadow-2xl flex flex-col group transition-all duration-300"
        >
            <div className="carbon-content w-full h-full flex flex-col">
                <div className="relative w-full pt-[56.25%] bg-black border-b border-pe-border dark:border-[#222] shadow-inner overflow-hidden">
                    {previewSrc ? (
                        <img
                            src={previewSrc}
                            alt={item.title}
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                            loading="lazy"
                        />
                    ) : (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-[#121214] to-black">
                            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#8D6435]/70 dark:text-[#E6C299]/70 border border-[#8D6435]/25 dark:border-[#E6C299]/25 px-3 py-1.5 rounded">
                                {fileTypeBadge(item.fileType)}
                            </span>
                            <span className="text-stone-500 dark:text-slate-600 text-[9px] font-bold uppercase tracking-widest">
                                Kein Vorschaubild
                            </span>
                        </div>
                    )}
                    <span className="absolute top-3 right-3 text-[9px] font-black uppercase tracking-widest bg-black/70 text-[#E6C299] border border-[#E6C299]/30 px-2 py-1 rounded backdrop-blur-sm">
                        {fileTypeBadge(item.fileType)}
                    </span>
                </div>
                <div className="p-5 flex flex-col flex-1 gap-4">
                    <h5 className="text-lg font-black text-pe-text dark:text-white group-hover:text-[#8D6435] dark:group-hover:text-[#E6C299] transition-colors tracking-wide uppercase leading-tight">
                        {item.title}
                    </h5>
                    {item.description && (
                        <p className="text-pe-muted dark:text-slate-400 text-sm leading-relaxed flex-1">{item.description}</p>
                    )}
                    <a
                        href={item.fileUrl}
                        download={item.fileName || true}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-auto inline-flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black text-[10px] font-black uppercase tracking-widest transition-colors"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>
                        Herunterladen
                        {item.fileName ? ` · ${item.fileName}` : ''}
                    </a>
                </div>
            </div>
        </div>
        );
    };

    return (
        <Page>
            <AcademyStyles />
            <div className="academy-carbon-shell bg-deep-space min-h-screen font-sans text-pe-text dark:text-slate-200 selection:bg-[#E6C299] selection:text-black pb-12 p-4 sm:p-8">
                <Section> 
                    <PageTitle>
                        <span className="tracking-widest uppercase text-pe-text dark:text-white">Academy</span>
                    </PageTitle>

                    <div className="flex flex-wrap gap-2 mt-6 mb-2 border-b border-pe-border dark:border-[#222] pb-4">
                        <button
                            type="button"
                            onClick={() => setActiveTab('videos')}
                            className={`px-4 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors border ${
                                activeTab === 'videos'
                                    ? 'bg-[#E6C299] text-black border-[#E6C299]'
                                    : 'bg-transparent text-pe-muted dark:text-slate-400 border-pe-border-strong dark:border-[#333] hover:border-[#8D6435]/40 dark:hover:border-[#E6C299]/40 hover:text-pe-text dark:hover:text-white'
                            }`}
                        >
                            Video-Tutorials
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('downloads')}
                            className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border flex items-center gap-2 ${
                                activeTab === 'downloads'
                                    ? 'bg-[#E6C299] text-black border-[#E6C299] shadow-[0_0_22px_rgba(230,194,153,0.45)]'
                                    : 'bg-[#E6C299]/15 text-[#E6C299] border-[#E6C299]/70 shadow-[0_0_18px_rgba(230,194,153,0.28)] hover:bg-[#E6C299]/25 hover:border-[#E6C299] hover:shadow-[0_0_28px_rgba(230,194,153,0.45)]'
                            }`}
                        >
                            <Download className="w-3.5 h-3.5" />
                            Downloads & Materialien
                        </button>
                    </div>

                    {activeTab === 'videos' && (
                        <>
                    {tutorialCategories.map((categoryObj: TutorialCategory) => {
                        const categoryName = categoryObj.name;
                        const categoryVideos = videosByCategory[categoryName] || [];
                        
                        return (
                            <div key={categoryName} className="mt-12 first:mt-8">
                                <div className="flex items-center gap-3 border-t border-pe-border dark:border-[#222] pt-8 mb-6">
                                    <span className="w-2 h-8 rounded-full inline-block bg-[#E6C299] shadow-[0_0_10px_rgba(230,194,153,0.5)]"></span>
                                    <h4 className="text-xl lg:text-2xl font-black text-pe-text dark:text-white tracking-widest uppercase m-0">
                                        {categoryName}
                                    </h4>
                                </div>
                                
                                {categoryVideos.length > 0 ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {categoryVideos.map(renderVideoCard)}
                                    </div>
                                ) : (
                                    <div className="carbon-panel carbon-weave-bg border border-pe-border dark:border-[#222] rounded-xl p-8 text-center shadow-lg opacity-70">
                                        <div className="carbon-content flex flex-col items-center justify-center gap-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-stone-500 dark:text-slate-600 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                            <p className="text-pe-muted dark:text-slate-400 font-bold uppercase tracking-widest text-xs">Hier erscheinen in Kürze neue Inhalte.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {orphanVideos.length > 0 && (
                        <div className="mt-12">
                            <h4 className="text-xl lg:text-2xl font-black text-stone-500 dark:text-slate-500 mb-6 flex items-center gap-3 border-t border-pe-border dark:border-[#222] pt-8 tracking-widest uppercase">
                                <span className="bg-stone-300 dark:bg-slate-700 w-2 h-8 rounded-full inline-block"></span>
                                {'Sonstige & Ohne Rubrik'}
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {orphanVideos.map(renderVideoCard)}
                            </div>
                        </div>
                    )}
                        </>
                    )}

                    {activeTab === 'downloads' && (
                        <>
                            {downloadItems.length === 0 ? (
                                <div className="mt-10 carbon-panel carbon-weave-bg border border-pe-border dark:border-[#222] rounded-xl p-10 text-center shadow-lg opacity-80">
                                    <div className="carbon-content flex flex-col items-center gap-3">
                                        <p className="text-pe-muted dark:text-slate-400 font-bold uppercase tracking-widest text-xs">
                                            Noch keine Download-Materialien hinterlegt.
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {downloadCategories.map((categoryObj) => {
                                        const categoryName = categoryObj.name;
                                        const items = downloadsByCategory[categoryName] || [];
                                        if (items.length === 0) return null;
                                        return (
                                            <div key={`dl-${categoryName}`} className="mt-12 first:mt-8">
                                                <div className="flex items-center gap-3 border-t border-pe-border dark:border-[#222] pt-8 mb-6">
                                                    <span className="w-2 h-8 rounded-full inline-block bg-[#E6C299] shadow-[0_0_10px_rgba(230,194,153,0.5)]" />
                                                    <h4 className="text-xl lg:text-2xl font-black text-pe-text dark:text-white tracking-widest uppercase m-0">
                                                        {categoryName}
                                                    </h4>
                                                </div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                    {items.map(renderDownloadCard)}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {orphanDownloads.length > 0 && (
                                        <div className="mt-12">
                                            <h4 className="text-xl lg:text-2xl font-black text-stone-500 dark:text-slate-500 mb-6 flex items-center gap-3 border-t border-pe-border dark:border-[#222] pt-8 tracking-widest uppercase">
                                                <span className="bg-stone-300 dark:bg-slate-700 w-2 h-8 rounded-full inline-block" />
                                                Sonstige & Ohne Rubrik
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                {orphanDownloads.map(renderDownloadCard)}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}

                </Section>
            </div>
        </Page>
    );
};

export default AcademyPage;